import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  ButtonGroup,
  Heading,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { deleteDocument, getDocument, getDocuments } from '../../api/api';
import PatientsAdd from '../Patients/PatientsAdd';
import LabsAdd from '../Labs/LabsAdd';
import { getAge } from '../../Helpers/UserHelpers';

function AdminUserPatient({ currentUser, setCurrentUser }) {
  const navigate = useNavigate();
  const { id, patientId } = useParams();
  const toast = useToast();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isOrderOpen, setIsOrderOpen] = useState(false);
  const [patient, setPatient] = useState(null);
  const [labs, setLabs] = useState(null);
  const [filteredLabs, setFilteredLabs] = useState(null);
  const [isResending, setIsResending] = useState(false);
  const [selectedLabId, setSelectedLabId] = useState(null);
  const [selectedLab, setSelectedLab] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = React.useRef();

  const handleResend = lab => {
    setIsResending(true);
    setSelectedLabId(lab?.id);
    setSelectedLab(lab);
    setTimeout(() => {
      //submit google form to trigger email
      document.querySelector('#submitconsentformtwo').click();
    }, 1000);

    setTimeout(() => {
      setSelectedLabId(null);
      setSelectedLab(null);
      setIsResending(false);
      toast({
        title: `Consent Email sent to ${lab?.patient?.email}`,
        description: `Disclosure forms were included in the email.`,
        status: 'success',
        duration: 9000,
        isClosable: true,
      });
    }, 3000);
  };

  const handleDelete = () => {
    deleteDocument(id, 'patients', patientId, '/patients', navigate);
  };

  useEffect(() => {
    getDocument(id, 'patients', patientId, setPatient);
    getDocuments(id, 'labs', setLabs);
  }, [id]);

  useEffect(() => {
    if (labs) {
      const fLabs = labs.filter(l => l.patientId === patientId);
      setFilteredLabs(fLabs);
    }
  }, [labs]);

  return (
    <div id="patients" className="page-padding patients-detail">
      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Patient
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure? You can't undo this action afterwards.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={handleDelete} ml={3}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      <PatientsAdd
        currentUser={currentUser}
        adminSelectedUserId={id}
        setCurrentUser={setCurrentUser}
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
        patient={patient}
      />
      <LabsAdd
        currentUser={currentUser}
        adminSelectedUserId={id}
        setCurrentUser={setCurrentUser}
        isOpen={isOrderOpen}
        setIsOpen={setIsOrderOpen}
        patient={patient}
      />
      <Heading
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={4}
        fontSize={{
          base: '4xl',
          md: '5xl',
        }}
      >
        {patient?.firstName} {patient?.lastName}
        <ButtonGroup>
          <Button colorScheme="gray" onClick={() => onOpen()}>
            Delete
          </Button>
          <Button colorScheme="blue" onClick={() => setIsModalOpen(true)}>
            Edit
          </Button>
          <Button colorScheme="blue" onClick={() => setIsOrderOpen(true)}>
            Order Lab
          </Button>
        </ButtonGroup>
      </Heading>
      <TableContainer>
        <Table variant="simple">
          {patient && (
            <Tbody>
              {/* <Tr>
                <Td>Id</Td>
                <Td className="id">{patient.id}</Td>
              </Tr> */}

              <Tr>
                <Td>Date of Birth</Td>
                <Td className="dob">
                  {patient?.dob} (Age: {getAge(new Date(patient?.dob))} yrs old)
                </Td>
              </Tr>
              <Tr>
                <Td>Contact Info</Td>
                <Td className="email">
                  <a
                    style={{ marginLeft: -15 }}
                    href={`mailto:${patient?.email}`}
                  >
                    <Button colorScheme="blue" variant="ghost">
                      {patient.email}
                    </Button>
                  </a>
                  <a href={`mailto:${patient?.mobileWithCarrier}`}>
                    <Button colorScheme="blue" variant="ghost">
                      {patient?.mobile}{' '}
                      {patient.mobileCarrier
                        ? `with ${patient.mobileCarrier}`
                        : ''}
                    </Button>
                  </a>
                </Td>
              </Tr>
              <Tr>
                <Td>Parent Email</Td>
                <Td className="email">
                  <a
                    style={{ marginLeft: -15 }}
                    href={`mailto:${patient?.parentsEmail}`}
                  >
                    <Button colorScheme="blue" variant="ghost">
                      {patient.parentsEmail}
                    </Button>
                  </a>
                </Td>
              </Tr>
              <Tr>
                <Td>Address</Td>
                <Td className="address">{patient?.address}</Td>
              </Tr>
              <Tr>
                <Td>Labs</Td>
                <Td>
                  <TableContainer>
                    <Table size="sm">
                      <Thead>
                        <Tr>
                          <Th>Id</Th>
                          <Th>Date</Th>
                          <Th>Status</Th>
                          <Th>Actions</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {filteredLabs?.map(l => (
                          <Tr key={l.id}>
                            <Td>
                              <Button
                                size="sm"
                                colorScheme="blue"
                                variant="ghost"
                                onClick={() => navigate(`/lab/${l.id}`)}
                                style={{ marginLeft: -12 }}
                              >
                                {l.id}
                              </Button>
                            </Td>
                            <Td>{new Date(l.date).toLocaleDateString()}</Td>
                            <Td>{l.status}</Td>
                            <Td>
                              <Button
                                key={l.id}
                                colorScheme="gray"
                                size="sm"
                                isLoading={
                                  isResending && selectedLabId === l.id
                                }
                                loadingText="Resending Patient Consent Form"
                                onClick={() => handleResend(l)}
                              >
                                Resend Patient Consent Form
                              </Button>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </Td>
              </Tr>
              <Tr>
                <Td>Notes</Td>
                <Td
                  className="note"
                  dangerouslySetInnerHTML={{ __html: patient?.note }}
                ></Td>
              </Tr>
            </Tbody>
          )}
        </Table>
      </TableContainer>
      <br />

      <iframe
        title="secret-frame-two"
        name="secret-frame-two"
        width="0"
        height="0"
        border="0"
        style={{ display: 'none' }}
      ></iframe>
      <form
        className="form"
        action="https://docs.google.com/forms/d/e/1FAIpQLSd0siGl6pf4z1J7kEdxk6mzAqZo0YTd_pULjJMNBa9gk38B6Q/formResponse"
        id="ss-form2"
        method="POST"
        target="secret-frame-two"
        style={{ display: 'none' }}
      >
        <div className="form-group">
          <label htmlFor="dateValue">Date</label>
          <input
            name="entry.1726393305"
            readOnly
            type="text"
            value={selectedLab?.date}
          />
        </div>
        <div className="form-group">
          <label htmlFor="labId">LabId</label>
          <input
            name="entry.1252597670"
            readOnly
            type="text"
            value={selectedLab?.id}
          />
        </div>
        <div className="form-group">
          <label htmlFor="patientId">PatientId</label>
          <input
            name="entry.2107253374"
            readOnly
            type="text"
            value={patient?.id}
          />
        </div>
        <div className="form-group">
          <label htmlFor="patientEmail">PatientEmail</label>
          <input
            name="entry.235882463"
            readOnly
            type="email"
            value={patient?.email}
          />
        </div>
        <div className="form-group">
          <label htmlFor="patientFirstName">PatientFirstName</label>
          <input
            name="entry.1702512101"
            readOnly
            type="text"
            value={patient?.firstName}
          />
        </div>
        <div className="form-group">
          <label htmlFor="doctorName">DoctorName</label>
          <input
            name="entry.850389264"
            readOnly
            type="text"
            value={currentUser?.displayName}
          />
        </div>
        <div className="form-group">
          <label htmlFor="type">Type</label>
          <input name="entry.351738770" readOnly type="text" value={''} />
        </div>
        <input
          id="submitconsentformtwo"
          type="submit"
          value="Submit Consent Form Two"
        />
      </form>
    </div>
  );
}

export default AdminUserPatient;
